import { CommonModule } from '@angular/common';
import { NgModule, Type } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { PermissionDirective } from '@app/directive/permission.directive';
import { RemoteSelectComponent } from '@app/shared/components/remote-select/remote-select.component';
import { ResourceConnectionChartComponent } from '@app/shared/components/resource-connection-chart/resource-connection-chart.component';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { SHARED_ZORRO_MODULES } from './shared-zorro.module';

import { DragDropModule } from '@angular/cdk/drag-drop';
import { CalendarDialogComponent } from '@app/shared/components/calendar-dialog/calendar-dialog.component';
import { ProjectCalendarComponent } from '@app/shared/components/project-calendar/project-calendar.component';
import { ResourceMenuComponent } from '@app/shared/components/resource-menu/resource-menu.component';
import { FullCalendarModule } from '@fullcalendar/angular';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import * as echarts from 'echarts';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzCascaderModule } from 'ng-zorro-antd/cascader';
import { NgxEchartsModule } from 'ngx-echarts';
import { HighLightPipe } from '../pipe/high-light.pipe';
import { SelectShowPipe } from '../pipe/select-show.pipe';
import { CapitalizePipe } from '../pipe/capitalize.pipe';
import { AccountListComponent } from './components/account-list/account-list.component';
import { ApplicationComponent } from './components/application/application.component';
import { ApplicationDetailComponent } from './components/application/components/application-detail/application-detail.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { CardComponent } from './components/card/card.component';
import { EngineerItemComponent } from './components/engineer-item/engineer-item.component';
import { FormInputComponent } from './components/form-input/form-input.component';
import { InputComponent } from './components/input/input.component';
import { LocationGroupComponent } from './components/location-group/location-group.component';
import { LocationSelectsComponent } from './components/location-selects/location-selects.component';
import { LocationComponent } from './components/location/location.component';
import { MatrixComponent } from './components/matrix/matrix.component';
import { MenuComponent } from './components/menu/menu.component';
import { NotificationDetailComponent } from './components/notification-list/components/notification-detail/notification-detail.component';
import { NotificationItemComponent } from './components/notification-list/components/notification-item/notification-item.component';
import { NotificationListComponent } from './components/notification-list/notification-list.component';
import { OverflowTooltipComponent } from './components/overflow-tooltip/overflow-tooltip.component';
import { ProgressCircleComponent } from './components/progress-circle/progress-circle.component';
import { ProgressComponent } from './components/progress/progress.component';
import { ProjectProgressComponent } from './components/project-progress/project-progress.component';
import { RangePickerComponent } from './components/range-picker/range-picker.component';
import { RecipientsTableComponent } from './components/recipients-table/recipients-table.component';
import { ResourceContentComponent } from './components/resource-content/resource-content.component';
import { ResourceExportComponent } from './components/resource-export/resource-export.component';
import { ScheduleSettingComponent } from './components/schedule-setting/schedule-setting.component';
import { SendEmailComponent } from './components/send-email/send-email.component';
import { StComponent } from './components/st/st.component';
import { StatusTagComponent } from './components/status-tag/status-tag.component';
import { TabButtonComponent } from './components/tab-button/tab-button.component';
import { CalendarLegendComponent } from './components/table-calendar/component/calendar-legend/calendar-legend.component';
import { EditReservationComponent } from './components/table-calendar/component/edit-reservation/edit-reservation.component';
import { TableCalendarComponent } from './components/table-calendar/table-calendar.component';
import { TabsComponent } from './components/tabs/tabs.component';
import { TagComponent } from './components/tag/tag.component';
import { TaskChartComponent } from './components/task-chart/task-chart.component';
import { TestCaseProgressComponent } from './components/test-case-progress/test-case-progress.component';
import { TimelineComponent } from './components/timeline/timeline.component';
import { TooltipButtonComponent } from './components/tooltip-button/tooltip-button.component';
import { UploadFileComponent } from './components/upload-file/upload-file.component';
import { UploadComponent } from './components/upload/upload.component';
import { ViewTestCaseProgressComponent } from './components/view-test-case-progress/view-test-case-progress.component';
import { SelectTemplateComponent } from './components/select-template/select-template.component';
import { MultipleInputSearchComponent } from './components/multiple-input-search/multiple-input-search.component';
import { ResourceSelectComponent } from './components/resource-select/resource-select.component';
import { ConfirmDialogComponent } from './components/confirm-dialog/confirm-dialog.component';
import { CommentComponent } from './components/comment/comment.component';
import { TinymceEditorComponent } from './components/tinymce-editor/tinymce-editor.component';
import { EditorModule } from '@tinymce/tinymce-angular';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FileListComponent } from './components/file-list/file-list.component';
import { ViewDialogComponent } from './components/view-dialog/view-dialog.component';
import { ZInputComponent } from './components/z-input/z-input.component';
import { AvatarGroupComponent } from '@app/shared/components/avatar-group/avatar-group.component';
import { CapitalizeLowerPipe } from '@app/pipe/capitalize-lower.pipe';
import { OverflowTextPipe } from '@app/pipe/overflow-text.pipe';
import { OverflowNumTooltipComponent } from '@app/shared/components/overflow-num-tooltip/overflow-num-tooltip.component';
import { ZUrlComponent } from './components/z-url/z-url.component';
import { EmailRecipientsComponent } from '@app/shared/components/email-recipients/email-recipients.component';
import { NzResizableModule } from 'ng-zorro-antd/resizable';

// FullCalendarModule.registerPlugins([ // register FullCalendar plugins
//     resourceTimelinePlugin,
//     interactionPlugin,
//     dayGridPlugin
// ]);

const CustomComponents = [
    CardComponent,
    ApplicationComponent,
    TabsComponent,
    OverflowTooltipComponent,
    ProgressComponent,
    EngineerItemComponent,
    NotificationItemComponent,
    NotificationListComponent,
    NotificationDetailComponent,
    AvatarComponent,
    MenuComponent,
    UploadComponent,
    TabButtonComponent,
    MatrixComponent,
    AccountListComponent,
    ApplicationDetailComponent,
    TagComponent,
    StComponent,
    SelectShowPipe,
    UploadFileComponent,
    HighLightPipe,
    CapitalizePipe,
    RemoteSelectComponent,
    StatusTagComponent,
    InputComponent,
    TimelineComponent,
    LocationComponent,
    CalendarDialogComponent,
    ProjectCalendarComponent,
    ResourceMenuComponent,
    LocationGroupComponent,
    RangePickerComponent,
    ProgressCircleComponent,
    ResourceConnectionChartComponent,
    ProjectProgressComponent,
    CalendarLegendComponent,
    TaskChartComponent,
    TooltipButtonComponent,
    TableCalendarComponent,
    EditReservationComponent,
    ScheduleSettingComponent,
    RecipientsTableComponent,
    SendEmailComponent,
    FileListComponent,
    ZInputComponent,
    ZUrlComponent,
    AvatarGroupComponent,
    CapitalizeLowerPipe,
    OverflowTextPipe,
    OverflowNumTooltipComponent,
    EmailRecipientsComponent
];

const directives = [PermissionDirective];
// #region third libs

const THIRDMODULES: Type<any>[] = [];

// #endregion

// #region your componets & directives

const COMPONENTS: Type<any>[] = [];
const DIRECTIVES: Type<any>[] = [];

// #endregion

@NgModule({
    declarations: [
        ...COMPONENTS,
        ...DIRECTIVES,
        ...CustomComponents,
        ...directives,
        LocationSelectsComponent,
        TestCaseProgressComponent,
        ViewTestCaseProgressComponent,
        FormInputComponent,
        ResourceContentComponent,
        ResourceExportComponent,
        SelectTemplateComponent,
        MultipleInputSearchComponent,
        ResourceSelectComponent,
        ConfirmDialogComponent,
        CommentComponent,
        TinymceEditorComponent,
        FileListComponent,
        ViewDialogComponent,
    ],
    exports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        ...SHARED_ZORRO_MODULES,
        ...THIRDMODULES,
        ...COMPONENTS,
        ...DIRECTIVES,
        ...CustomComponents,
        ...directives,
        TaskChartComponent,
        TooltipButtonComponent,
        TableCalendarComponent,
        ScheduleSettingComponent,
        SendEmailComponent,
        LocationSelectsComponent,
        TestCaseProgressComponent,
        FormInputComponent,
        ResourceContentComponent,
        SelectTemplateComponent,
        MultipleInputSearchComponent,
        ResourceSelectComponent,
        ConfirmDialogComponent,
        CommentComponent,
        TinymceEditorComponent,
        FileListComponent,
        ViewDialogComponent,
        EmailRecipientsComponent,
        NzResizableModule
    ],
    imports: [
        CommonModule,
        FormsModule,
        EditorModule,
        RouterModule,
        ReactiveFormsModule,
        ...SHARED_ZORRO_MODULES,
        ...THIRDMODULES,
        NzPaginationModule,
        NzCollapseModule,
        FullCalendarModule,
        NzEmptyModule,
        NzCascaderModule,
        NzAffixModule,
        NgxEchartsModule.forRoot({
            echarts,
        }),
        DragDropModule,
        NzResizableModule
    ],
    providers: [
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class SharedModule {}
